import { BaseElementWithoutShadowDOM } from '@theme/base-element'
import '@theme/ui-mega-menu'
import { html } from '@theme/vendor-lit'

export class MainHeader extends BaseElementWithoutShadowDOM {
  #resizeObserver = new ResizeObserver(() => this.#setHeightCSSProperty())

  constructor() {
    super()

    this.headerBounds = {}
    this.currentScrollTop = 0
    this._state = -1
    this._height = 0
    this.headerGroup = this.querySelector('.header-group')
    this.filterBar = document.getElementById('main-collection-filters')
  }

  #setHeightCSSProperty() {
    if (this._state === 1) {
      document.documentElement.style.setProperty('--header-height', '0px')
      return
    }
    const announcementBarEl = this.querySelector('.announcement-bar')
    const headerEl = this.querySelector('header')
    const mobileNavEl = this.querySelector('[data-mobile-nav]')

    const { height: announcementBarHeight } =
      announcementBarEl?.getBoundingClientRect() || { height: 0 }
    const { height: headerHeight } = headerEl?.getBoundingClientRect() || {
      height: 0,
    }
    const { height: mobileNavHeight } =
      mobileNavEl?.getBoundingClientRect() || {
        height: 0,
      }

    document.documentElement.style.setProperty(
      '--announcement-bar-height',
      `${announcementBarHeight}px`
    )

    document.documentElement.style.setProperty(
      '--mobile-nav-height',
      `${mobileNavHeight}px`
    )

    this._state = 0
    this._height = announcementBarHeight + headerHeight + mobileNavHeight

    document.documentElement.style.setProperty(
      '--header-height',
      `${this._height}px`
    )

    this.requestUpdate()
  }

  /**
   * The header's height is calculated and space is preserved before
   * applying fixed positioning.
   */
  #makeHeaderSticky() {
    document.querySelector('.fixed-main-header')?.classList.add('fixed')
  }

  firstUpdated(_changedProperties) {
    super.firstUpdated(_changedProperties)
    this.#setHeightCSSProperty()
    this.#makeHeaderSticky()

    this.#resizeObserver.observe(this)
    this.onScrollHandler = this.onScroll.bind(this)
    window.addEventListener('scroll', this.onScrollHandler)
    this.createObserver()
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect()
    super.disconnectedCallback()
    window.removeEventListener('scroll', this.onScrollHandler)
  }

  createObserver() {
    let observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect
      observer.disconnect()
    })

    observer.observe(this)
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop

    if (
      scrollTop > this.currentScrollTop &&
      scrollTop > this.headerBounds.bottom
    ) {
      requestAnimationFrame(this.hide.bind(this))
    } else if (
      scrollTop < this.currentScrollTop &&
      scrollTop > this.headerBounds.bottom
    ) {
      requestAnimationFrame(this.reveal.bind(this))
    } else if (scrollTop <= this.headerBounds.top) {
      requestAnimationFrame(this.reset.bind(this))
    }

    this.currentScrollTop = scrollTop
  }

  reset() {
    this.headerGroup.classList.remove('-translate-y-full')
    this.headerGroup.classList.remove('duration-700')
    if (this._state === 0) {
      return
    }

    this.#setHeightCSSProperty()
    document.documentElement.style.setProperty(
      '--header-height',
      `${this._height}px`
    )

    this._state = 0
  }

  hide() {
    if (this._state === 1) {
      return
    }
    this._state = 1

    setTimeout(function () {
      document
        .querySelector(':root')
        .style.setProperty(`--header-height`, `0px`)
    }, 100)

    const predictiveSearchElement = this.querySelector('[data-search]')

    if (!predictiveSearchElement?.hasAttribute('open')) {
      this.headerGroup.classList.add('-translate-y-full')
      this.headerGroup.classList.add('duration-700')
    }
    this.headerGroup.querySelectorAll('[menu-trigger]').forEach((item) => {
      item.setAttribute('aria-expanded', 'false')
    })
  }

  reveal() {
    this.headerGroup.classList.remove('-translate-y-full')
    this.headerGroup.classList.remove('duration-700')
    if (this._state === 2) {
      return
    }

    this.#setHeightCSSProperty()

    document
      .querySelector(':root')
      .style.setProperty(`--header-height`, `${this._height}px`)

    this._state = 2
  }

  render() {
    return html`
      <div
        class="bg-transparent relative z-0 w-full translate-y-0 transition-transform duration-500"
        style="
             transform: ${
          this._state === 1 ? 'translateY(-100%)' : 'translateY(0)'
        };
             height: ${this._height}px"
      ></div>
    `
  }
}

window.customElements.define('main-header', MainHeader)
